import { i18n, type Messages } from "@lingui/core";

import { messages as enMessages } from "./locales/en/messages";
import { messages as esMessages } from "./locales/es/messages";
import { messages as frMessages } from "./locales/fr/messages";
import { messages as deMessages } from "./locales/de/messages";
import { messages as ptMessages } from "./locales/pt/messages";
import { messages as zhMessages } from "./locales/zh/messages";
import { messages as plMessages } from "./locales/pl/messages";
import { messages as jaMessages } from "./locales/ja/messages";
import { messages as elMessages } from "./locales/el/messages";

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  coerceToSupported,
  type SupportedLanguage,
} from "~/utils/i18n";

// On the server we share a single Lingui i18n singleton across requests,
// which is fine while we have one synchronous `activateLocale` per render
// and no in-flight async work that crosses the locale boundary. If/when we
// add async loaders that resolve mid-render, switch this to per-request
// `setupI18n()` instances (Lingui SSR docs) to avoid one request's locale
// leaking into another's response stream.
const CATALOGS: Record<SupportedLanguage, Messages> = {
  en: enMessages,
  fr: frMessages,
  es: esMessages,
  de: deMessages,
  pt: ptMessages,
  zh: zhMessages,
  pl: plMessages,
  ja: jaMessages,
  el: elMessages,
};

let loaded = false;

function loadAllCatalogs() {
  for (const locale of SUPPORTED_LANGUAGES) {
    // Merge the English source catalog under every non-English locale so a
    // message that is missing from a target catalog — added but not yet
    // translated, or whose empty translation was dropped by `lingui compile` —
    // renders the English text instead of its raw Lingui hash id (e.g.
    // "5YF3vd"). The CI guard (tools/lint/check-i18n-extracted.mjs) blocks the
    // worse case where a message is missing from *every* catalog; this is the
    // runtime safety net for the translated-but-incomplete case.
    i18n.load(
      locale,
      locale === DEFAULT_LANGUAGE
        ? CATALOGS[locale]
        : { ...CATALOGS[DEFAULT_LANGUAGE], ...CATALOGS[locale] },
    );
  }
  loaded = true;
}

export function activateLocale(value: string | null | undefined): SupportedLanguage {
  if (!loaded) loadAllCatalogs();
  const locale = coerceToSupported(value);
  i18n.activate(locale);
  return locale;
}

if (!loaded) {
  loadAllCatalogs();
  i18n.activate(DEFAULT_LANGUAGE);
}

export { i18n };
